import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Users, MessageSquare, Building2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useTeamAuth } from "@/lib/teamAuth";
import TeamShell from "@/components/TeamShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamDashboard() {
    const { member, token, loading } = useTeamAuth();
    const [busy, setBusy] = useState(true);
    const [clients, setClients] = useState([]);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    useEffect(() => {
        if (!token) return;
        setBusy(true);
        axios
            .get(`${API}/team/clients`, { headers })
            .then((r) => setClients(r.data || []))
            .catch(() => toast.error("Could not load assigned clients"))
            .finally(() => setBusy(false));
    }, [token, headers]);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (!member) return <Navigate to="/team/login" replace />;

    const totalUnread = clients.reduce((acc, c) => acc + (c.unread_count || 0), 0);
    const totalProjects = clients.reduce((acc, c) => acc + (c.project_count || 0), 0);

    return (
        <TeamShell>
            <div className="mb-10">
                <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-3">
                    Manager dashboard
                </div>
                <h1 className="font-display text-bone text-5xl tracking-tightest leading-[1.05]">
                    Hi, {member.name.split(" ")[0]}.
                </h1>
                <p className="text-bone-500 text-base mt-3 max-w-xl">
                    Your assigned clients and projects. Email and billing details
                    are kept private — coordinate over chat.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <Stat icon={Users} label="Assigned clients" value={clients.length} />
                <Stat icon={Building2} label="Active projects" value={totalProjects} />
                <Stat icon={MessageSquare} label="Unread messages" value={totalUnread} accent={totalUnread > 0} />
            </div>

            {busy ? (
                <div className="flex items-center gap-3 text-bone-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading clients…
                </div>
            ) : clients.length === 0 ? (
                <div className="border border-white/10 bg-ink-800/40 p-10 text-center">
                    <Users className="w-10 h-10 text-bone-500 mx-auto mb-4" />
                    <h3 className="font-display text-2xl text-bone mb-2">No clients assigned yet.</h3>
                    <p className="text-sm text-bone-500">
                        Once a client is assigned to you, they'll appear here.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {clients.map((c) => (
                        <Link
                            key={c.id}
                            to={`/team/clients/${c.id}`}
                            data-testid={`team-client-card-${c.id}`}
                            className="group border border-white/10 bg-ink-800/40 p-6 hover:border-ember/40 transition-colors"
                        >
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div>
                                    <h3 className="font-display text-bone text-2xl tracking-tight">
                                        {c.name}
                                    </h3>
                                    {c.company && (
                                        <p className="text-sm text-bone-500 mt-1">{c.company}</p>
                                    )}
                                </div>
                                {c.unread_count > 0 && (
                                    <span
                                        data-testid={`team-client-unread-${c.id}`}
                                        className="inline-flex items-center gap-1.5 px-2 py-1 border border-ember bg-ember/10 text-ember text-[10px] uppercase tracking-[0.2em]"
                                    >
                                        <MessageSquare className="w-3 h-3" />
                                        {c.unread_count} new
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center justify-between text-xs text-bone-500">
                                <div className="flex items-center gap-3">
                                    {c.plan && (
                                        <span className="px-2 py-1 border border-white/10 uppercase tracking-[0.2em] text-[10px]">
                                            {c.plan}
                                        </span>
                                    )}
                                    <span>{c.project_count || 0} projects</span>
                                </div>
                                <ArrowUpRight className="w-4 h-4 text-bone-500 group-hover:text-ember transition-colors" />
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </TeamShell>
    );
}

function Stat({ icon: Icon, label, value, accent = false }) {
    return (
        <div className={`border ${accent ? "border-ember/40 bg-ember/5" : "border-white/10 bg-ink-800/40"} p-5`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                <Icon className={`w-3.5 h-3.5 ${accent ? "text-ember" : ""}`} />
                {label}
            </div>
            <div className={`font-display text-4xl tracking-tightest ${accent ? "text-ember" : "text-bone"}`}>
                {value}
            </div>
        </div>
    );
}
